/**
 * TEND in-app agent — the gateway's /api/agent routes (phase 1).
 *
 * Everything goes through authedFetch (current token per call, one refresh
 * on an auth-shaped 401). The turn is Server-Sent Events over a POST, which
 * EventSource cannot do, so streamTurn reads the body as a stream and
 * parses the frames itself (parseSse is exported for its test).
 */
import { API_BASE_URL } from '../config/api';
import { authedFetch } from '../utils/authedFetch';

export type AgentSpeed = 'fast' | 'balanced' | 'best';
export type DraftState = 'pending' | 'confirmed' | 'skipped';

export interface AgentSettings {
  enabled: boolean;
  allow_editors: boolean;
  default_speed: AgentSpeed;
  /** The standing policy (QA bucket 1): {class: true} per class the agent may do without an approval card. */
  auto_confirm: Record<string, boolean>;
  /** Dollars per day for this brand's agent; 0 = no limit. Warning at warn_at, refusal at 100 %. */
  daily_budget_usd: number;
  idle_rollover_hours: number;
  /** Phase 4: Auto Tend runs the sweep may start per hour on the brand; 0 = no cap. */
  auto_runs_per_hour: number;
}

/** Phase 4: the Auto ladder as the routes report it. */
export interface AgentAutoStatus {
  tend: boolean;
  queue: boolean;
  since: string | null;
  sweep: boolean;
  waiting: number;
  running: number;
  today: Record<string, number>;
  last_hour: number;
  per_hour: number;
}

export interface AgentAutoRunSummary {
  status: 'waiting' | 'running' | 'tended' | 'skipped' | 'failed' | 'cancelled';
  reason: string | null;
  items: number;
  queued: number;
  summary: string | null;
  content_id: number;
}

export type AgentSettingsScope = 'brand' | 'universal';

export interface AgentSpend {
  spent: number;
  by_user: { user_id: string; name: string; spent: number }[];
  auto: number;
  since: string;
}

/** GET/PUT /settings: the effective settings, the brand's own keys, the universal row, today's spend. */
export interface AgentSettingsResponse {
  brand: string;
  server_enabled: boolean;
  settings: AgentSettings;
  overrides: Partial<AgentSettings>;
  universal: AgentSettings;
  /** Superadmins and customer success set the standing policy. */
  can_set_policy?: boolean;
  classes?: string[];
  can_edit_universal: boolean;
  spend: AgentSpend;
  warn_at: number;
  auto?: AgentAutoStatus;
}

export interface LlmPriceRow {
  input: number;
  output: number;
  cache_read: number;
  cache_write: number;
  long_context_threshold: number | null;
  long_context_multiplier: number | null;
}

/** LLM Settings (universal, superadmin): speed map, fallback chain, price table. */
export interface LlmSettings {
  speeds: Record<AgentSpeed, string>;
  fallback: string[];
  prices: Record<string, LlmPriceRow>;
  uses: { id: string; label: string; chain: boolean; note: string }[];
  updated_at: string | null;
  catalog: string[];
  can_edit: boolean;
  defaults: { speeds: Record<AgentSpeed, string>; fallback: string[]; prices: Record<string, LlmPriceRow> };
}

export interface LlmSettingsPatch {
  speeds?: Partial<Record<AgentSpeed, string>>;
  fallback?: string[];
  /** A model set to null removes its row (a seeded model falls back to its published rates). */
  prices?: Record<string, Partial<LlmPriceRow> | null>;
}

export type PendingActionKind = 'enqueue' | 'take' | 'playbook' | 'tactic' | 'strategy' | 'guidelines' | 'colors';
export type PendingActionStatus = 'pending' | 'approved' | 'skipped' | 'expired';
export type AgentClass = 'drafts' | 'queue' | 'take' | 'playbooks' | 'tactics' | 'strategies' | 'guidelines' | 'colors';

export const AGENT_CLASSES: AgentClass[] = ['drafts', 'queue', 'take', 'playbooks', 'tactics', 'strategies', 'guidelines', 'colors'];
export const CLASS_LABEL: Record<string, string> = {
  drafts: 'drafts', queue: 'queue slots', take: 'Take requests', playbooks: 'playbook changes', tactics: 'tactic changes',
  strategies: 'strategy changes', guidelines: 'guideline changes', colors: 'color changes',
};

/** A unified diff for a config change card (QA bucket 1). */
export interface ActionDiff { text: string; added: number; removed: number; truncated: boolean; lines?: number }

/** A proposal card: the agent asked to queue an item, request a Take, or change a recipe or the colors; the person decides. */
export interface PendingAction {
  id: number;
  thread_id: number;
  kind: PendingActionKind;
  status: PendingActionStatus;
  /** The confirmation class the card belongs to, and its label for "stop asking". */
  class?: AgentClass;
  class_label?: string;
  payload: {
    item_id?: number;
    platform?: string | null;
    title?: string | null;
    placement?: 'back' | 'front';
    would?: 'queue' | 'move';
    line?: { reason?: string | null; open_now?: boolean | null; next_open_at?: string | null; in_line?: number } | null;
    would_hold?: { reason: string; message: string } | null;
    take_would_clear?: boolean;
    in_line?: boolean;
    note?: string | null;
    deep_link?: string | null;
    result?: Record<string, unknown> | null;
    // config changes (playbook · tactic · strategy · guidelines)
    layer?: 'brand' | 'universal';
    name?: string | null;
    tactic_id?: string | null;
    version_from?: string | null;
    version_to?: string | null;
    created?: boolean;
    creates_copy?: boolean;
    from_layer?: string | null;
    diff?: ActionDiff | null;
    // colors
    brand?: string | null;
    brand_id?: number | null;
    changes?: string[];
    summary?: string | null;
    /** Approved inside the turn by the standing policy or the thread's "stop asking". */
    auto?: boolean;
  } | null;
  text: string;
  outcome: string | null;
  decided_by: string | null;
  decided_at: string | null;
  created_at: string | null;
}

export interface AgentPrefs {
  speed: AgentSpeed | null;
  panel_cols: number | null;
  brand_default_speed: AgentSpeed;
  effective_speed: AgentSpeed;
}

export interface AgentStatus {
  enabled: boolean;
  available: boolean;
  reason?: 'ok' | 'server' | 'brand_off' | 'role';
  role?: string | null;
  can_manage?: boolean;
  settings?: AgentSettings;
  prefs?: AgentPrefs;
  speeds: AgentSpeed[];
  default_speed: AgentSpeed;
  models: Record<string, string>;
  chain?: string[];
  brand?: string;
  brand_id?: number;
  /** Phase 3: the brand's day. limit 0 = no limit. */
  /** since/timezone: the spend day is the publishing day (scheduler_timezone, Eastern by default), the same for every brand. */
  budget?: { limit: number; spent_today: number; warn_at: number; since?: string; timezone?: string };
  auto_confirm?: Record<string, boolean>;
  auto?: AgentAutoStatus;
}

export interface AgentThreadSummary {
  id: number;
  user_id: string | null;
  brand_id: number;
  kind: 'chat' | 'auto';
  title: string | null;
  speed: AgentSpeed | null;
  model_at_start: string | null;
  auto_confirm: boolean;
  /** QA bucket 1: the classes this thread approves without asking. */
  auto_classes?: string[];
  status: 'active' | 'idle' | 'done' | 'failed';
  last_message_at: string | null;
  usage: { input_tokens: number; output_tokens: number; cache_read_tokens: number; cache_write_tokens: number };
  cost_usd: number | null;
  created_at: string | null;
  updated_at: string | null;
  pending_count: number;
  open_actions?: number;
  source_content_id?: number | null;
  /** Phase 4: the Auto Tend run behind a kind 'auto' thread. */
  auto?: AgentAutoRunSummary | null;
}

export type AgentRenderStatus = 'none' | 'queued' | 'rendering' | 'completed' | 'failed';

/** Phase 2b: the payload render behind a playbook draft (null = nothing TEND renders). */
export interface AgentDraftRender {
  status: AgentRenderStatus;
  job_id: number | null;
  error: string | null;
  mode?: string | null;
  video_url: string | null;
  still_url: string | null;
  outputs: { url: string; kind: string; aspect_ratio: string | null; role: string | null; duration_seconds: number | null }[];
  started_at?: string | null;
  completed_at?: string | null;
  render_time_seconds?: number | null;
}

export interface AgentDraft {
  id: number;
  platform: string;
  tactic_id: string;
  content_format: string | null;
  content_mode: string | null;
  post_title: string | null;
  headline: string | null;
  primary_text: string;
  primary_image_url: string | null;
  media_count: number;
  aspect_ratio: string | null;
  source_title: string | null;
  source_content_id: number | null;
  take_requested: boolean;
  state: DraftState;
  agent_pending_at: string | null;
  created_at: string | null;
  rendered_overlay_url?: string | null;
  render?: AgentDraftRender | null;
  /** Phase 3: confirmed on arrival by the brand setting or the thread's 'stop asking'. */
  auto_confirmed?: boolean;
}

export type AgentUiBlock =
  | { type: 'text'; text: string }
  | { type: 'tool_use'; id: string; name: string; input: unknown }
  | { type: 'tool_result'; tool_use_id: string; ok: boolean };

export interface AgentThreadMessage {
  id: number;
  seq: number;
  role: 'user' | 'assistant' | 'system';
  model: string | null;
  created_at: string | null;
  blocks: AgentUiBlock[];
  /** Phase 3: the model that was asked for when this reply came from the fallback chain. */
  fallback_from?: string | null;
  cost_usd?: number | null;
}

export interface AgentThreadDetail {
  thread: AgentThreadSummary;
  messages: AgentThreadMessage[];
  tool_calls: Array<{ id: number; name: string; status: 'ok' | 'error'; duration_ms: number | null; tool_use_id: string | null }>;
  drafts: AgentDraft[];
  pending_actions?: PendingAction[];
  message_count: number;
}

export type AgentEvent =
  | { event: 'turn'; thread_id: number; model: string; speed: AgentSpeed; brand: string; chain?: string[]; resume?: boolean }
  | { event: 'text'; delta: string }
  | { event: 'tool_start'; id: string; name: string }
  | { event: 'tool_result'; id: string; name: string; ok: boolean; summary: string }
  | { event: 'needs_approval'; action: PendingAction }
  | { event: 'drafts'; items: AgentDraft[]; auto_confirmed?: boolean }
  | { event: 'fallback'; from: string; to: string; speed: AgentSpeed; reason: 'rate_limited' | 'overloaded' | 'unavailable' | 'timeout' }
  | { event: 'budget'; level: 'warning' | 'exhausted'; spent: number; limit: number; by_user: { user_id: string; name: string; spent: number }[]; auto: number }
  | { event: 'usage'; input_tokens: number; output_tokens: number; cache_read_input_tokens: number; cache_creation_input_tokens: number; cost_usd?: number | null; model?: string }
  | { event: 'done'; stop_reason: string | null; pending_count: number; rounds: number; open_actions?: number; cost_usd?: number; budget?: { limit: number; spent_today: number } }
  | { event: 'error'; message: string; status?: number };

export class AgentApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

const BASE = `${API_BASE_URL}/agent`;
const JSON_HEADERS = { 'Content-Type': 'application/json' };

const q = (brand: string) => `?brand=${encodeURIComponent(brand)}`;

async function take<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let detail = `Request failed (${res.status})`;
    try {
      const body = await res.json();
      detail = typeof body?.detail === 'string' ? body.detail : JSON.stringify(body?.detail ?? body);
    } catch (e) { /* no body */ }
    throw new AgentApiError(detail, res.status);
  }
  return res.json() as Promise<T>;
}

/** Split complete SSE frames off a buffer: returns the parsed events and the unparsed tail. */
export function parseSse(buffer: string): { events: AgentEvent[]; rest: string } {
  const events: AgentEvent[] = [];
  let rest = buffer.replace(/\r\n/g, '\n');
  for (;;) {
    const idx = rest.indexOf('\n\n');
    if (idx < 0) break;
    const frame = rest.slice(0, idx);
    rest = rest.slice(idx + 2);
    const data = frame
      .split('\n')
      .filter((line) => line.startsWith('data:'))
      .map((line) => line.slice(5).replace(/^ /, ''))
      .join('\n');
    if (!data) continue;
    try {
      events.push(JSON.parse(data) as AgentEvent);
    } catch (e) {
      // a torn frame never reaches here (we split on the blank line); skip anything unparseable
    }
  }
  return { events, rest };
}

export const agentApi = {
  status: (brand?: string) =>
    authedFetch(`${BASE}/status${brand ? q(brand) : ''}`).then((r) => take<AgentStatus>(r)),

  getSettings: (brand: string) =>
    authedFetch(`${BASE}/settings${q(brand)}`).then((r) => take<AgentSettingsResponse>(r)),

  /** A sparse patch; a key set to null clears that scope's value (brand follows universal; universal follows the default). */
  putSettings: (brand: string, patch: Partial<{ [K in keyof AgentSettings]: AgentSettings[K] | null }>, scope: AgentSettingsScope = 'brand') =>
    authedFetch(`${BASE}/settings${q(brand)}&scope=${scope}`, { method: 'PUT', headers: JSON_HEADERS, body: JSON.stringify(patch) })
      .then((r) => take<AgentSettingsResponse>(r)),

  getLlmSettings: () => authedFetch(`${BASE}/llm-settings`).then((r) => take<LlmSettings>(r)),

  putLlmSettings: (patch: LlmSettingsPatch) =>
    authedFetch(`${BASE}/llm-settings`, { method: 'PUT', headers: JSON_HEADERS, body: JSON.stringify(patch) })
      .then((r) => take<LlmSettings>(r)),

  /** Phase 3: decide a proposal card. The reply carries the outcome sentence; the panel then resumes the turn.
      stopAsking (QA bucket 1) also approves every later proposal of that class in the thread. */
  approveAction: (actionId: number, stopAsking = false) =>
    authedFetch(`${BASE}/pending/${actionId}/approve`, { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ stop_asking: stopAsking }) })
      .then((r) => take<{ action: PendingAction; message: string; resume: boolean; thread: AgentThreadSummary }>(r)),

  /** QA bucket 1: "approve and stop asking" for one class in a thread. */
  setAutoClass: (threadId: number, cls: AgentClass, on: boolean) =>
    authedFetch(`${BASE}/threads/${threadId}/auto-classes`, { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ cls, on }) })
      .then((r) => take<AgentThreadSummary>(r)),

  skipAction: (actionId: number) =>
    authedFetch(`${BASE}/pending/${actionId}/skip`, { method: 'POST' })
      .then((r) => take<{ action: PendingAction; message: string; resume: boolean; thread: AgentThreadSummary }>(r)),

  setAutoConfirm: (threadId: number, on: boolean) =>
    authedFetch(`${BASE}/threads/${threadId}/auto-confirm`, { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ on }) })
      .then((r) => take<AgentThreadSummary>(r)),

  getPrefs: (brand: string) => authedFetch(`${BASE}/prefs${q(brand)}`).then((r) => take<AgentPrefs>(r)),

  patchPrefs: (brand: string, patch: { speed?: AgentSpeed | 'default'; panel_cols?: number }) =>
    authedFetch(`${BASE}/prefs${q(brand)}`, { method: 'PATCH', headers: JSON_HEADERS, body: JSON.stringify(patch) })
      .then((r) => take<AgentPrefs>(r)),

  listThreads: (brand: string) =>
    authedFetch(`${BASE}/threads${q(brand)}`).then((r) => take<{ brand: string; threads: AgentThreadSummary[] }>(r)),

  newThread: (brand: string, speed?: AgentSpeed) =>
    authedFetch(`${BASE}/threads`, { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ brand, speed }) })
      .then((r) => take<AgentThreadSummary>(r)),

  activeThread: (brand: string, speed?: AgentSpeed) =>
    authedFetch(`${BASE}/threads/active`, { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ brand, speed }) })
      .then((r) => take<AgentThreadSummary>(r)),

  getThread: (threadId: number) => authedFetch(`${BASE}/threads/${threadId}`).then((r) => take<AgentThreadDetail>(r)),

  /** Pick an Auto Tend run up: a chat thread of yours that carries the run's transcript (so the model keeps what it read). */
  forkThread: (threadId: number) =>
    authedFetch(`${BASE}/threads/${threadId}/fork`, { method: 'POST' }).then((r) => take<AgentThreadSummary>(r)),

  confirmDraft: (itemId: number) =>
    authedFetch(`${BASE}/drafts/${itemId}/confirm`, { method: 'POST' }).then((r) => take<AgentDraft>(r)),

  skipDraft: (itemId: number) =>
    authedFetch(`${BASE}/drafts/${itemId}/skip`, { method: 'POST' }).then((r) => take<AgentDraft>(r)),

  /** Phase 2b: render the draft's payload again (a failed or never-rendered piece). */
  renderDraft: (itemId: number) =>
    authedFetch(`${BASE}/drafts/${itemId}/render`, { method: 'POST' }).then((r) => take<AgentDraft>(r)),

  confirmAll: (threadId: number, stopAsking = false) =>
    authedFetch(`${BASE}/threads/${threadId}/drafts/confirm-all`, { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ stop_asking: stopAsking }) })
      .then((r) => take<{ thread_id: number; confirmed: AgentDraft[]; pending_count: number; auto_confirm: boolean }>(r)),

  skipAll: (threadId: number) =>
    authedFetch(`${BASE}/threads/${threadId}/drafts/skip-all`, { method: 'POST' })
      .then((r) => take<{ thread_id: number; skipped: AgentDraft[]; pending_count: number }>(r)),

  /** One turn: POST, then read the SSE body frame by frame, calling onEvent for each. */
  async streamTurn(
    threadId: number,
    body: { text?: string; page_context?: string | null; speed?: AgentSpeed | null; resume?: boolean },
    onEvent: (event: AgentEvent) => void,
    signal?: AbortSignal
  ): Promise<void> {
    const res = await authedFetch(`${BASE}/threads/${threadId}/turns`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(body),
      signal,
    });
    if (!res.ok) {
      await take(res); // throws with the detail
      return;
    }
    if (!res.body) throw new AgentApiError('The server sent no stream', 500);
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const parsed = parseSse(buffer);
      buffer = parsed.rest;
      parsed.events.forEach(onEvent);
    }
    parseSse(`${buffer}\n\n`).events.forEach(onEvent);
  },
};

export default agentApi;
